import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { Location, Hive, Group, HiveFilters, LoadingState } from "@types";
import { locationsApi } from "@api/locations";

interface LocationsState {
  apiaries: Location[];
  groups: Group[];
  hives: Hive[];
  selectedHive: Hive | null;
  selectedApiary: Location | null;
  filters: HiveFilters;
  view: "xlView" | "mView" | "xsView";
  loadingState: LoadingState;
  error: string | null;
  apiaryEdited: boolean;
  hiveEdited: boolean;
}

const initialState: LocationsState = {
  apiaries: [],
  groups: [],
  hives: [],
  selectedHive: null,
  selectedApiary: null,
  filters: {
    search: "",
    attention: false,
    reminder: false,
    impression: [],
    group: "off",
    alert: false,
    overdue: false,
  },
  view: "xlView",
  loadingState: "idle",
  error: null,
  apiaryEdited: false,
  hiveEdited: false,
};

// Async thunks
export const fetchApiariesAndGroups = createAsyncThunk(
  "locations/fetchApiariesAndGroups",
  async (_, { rejectWithValue }) => {
    try {
      const [apiariesResponse, groupsResponse] = await Promise.all([
        locationsApi.getApiaries(),
        locationsApi.getGroups(),
      ]);

      return {
        apiaries: apiariesResponse.data.data || [],
        groups: groupsResponse.data.data || [],
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch data",
      );
    }
  },
);

export const createApiary = createAsyncThunk(
  "locations/createApiary",
  async (apiaryData: Partial<Location>, { rejectWithValue }) => {
    try {
      const response = await locationsApi.createApiary(apiaryData);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create apiary",
      );
    }
  },
);

export const updateApiary = createAsyncThunk(
  "locations/updateApiary",
  async (
    { id, data }: { id: number; data: Partial<Location> },
    { rejectWithValue },
  ) => {
    try {
      const response = await locationsApi.updateApiary(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update apiary",
      );
    }
  },
);

export const deleteApiary = createAsyncThunk(
  "locations/deleteApiary",
  async (id: number, { rejectWithValue }) => {
    try {
      await locationsApi.deleteApiary(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete apiary",
      );
    }
  },
);

export const createHive = createAsyncThunk(
  "locations/createHive",
  async (hiveData: Partial<Hive>, { rejectWithValue }) => {
    try {
      const response = await locationsApi.createHive(hiveData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create hive",
      );
    }
  },
);

export const updateHive = createAsyncThunk(
  "locations/updateHive",
  async (
    { id, data }: { id: number; data: Partial<Hive> },
    { rejectWithValue },
  ) => {
    try {
      const response = await locationsApi.updateHive(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update hive",
      );
    }
  },
);

export const deleteHive = createAsyncThunk(
  "locations/deleteHive",
  async (id: number, { rejectWithValue }) => {
    try {
      await locationsApi.deleteHive(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete hive",
      );
    }
  },
);

export const deleteGroup = createAsyncThunk(
  "locations/deleteGroup",
  async (id: number, { rejectWithValue }) => {
    try {
      await locationsApi.deleteGroup(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete group",
      );
    }
  },
);

const locationsSlice = createSlice({
  name: "locations",
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<HiveFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    setView: (state, action: PayloadAction<"xlView" | "mView" | "xsView">) => {
      state.view = action.payload;
    },
    setSelectedHive: (state, action: PayloadAction<Hive | null>) => {
      state.selectedHive = action.payload;
    },
    setSelectedApiary: (state, action: PayloadAction<Location | null>) => {
      state.selectedApiary = action.payload;
    },
    setApiaryEdited: (state, action: PayloadAction<boolean>) => {
      state.apiaryEdited = action.payload;
    },
    setHiveEdited: (state, action: PayloadAction<boolean>) => {
      state.hiveEdited = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch Apiaries and Groups
      .addCase(fetchApiariesAndGroups.pending, (state) => {
        state.loadingState = "pending";
        state.error = null;
      })
      .addCase(fetchApiariesAndGroups.fulfilled, (state, action) => {
        state.loadingState = "succeeded";
        state.apiaries = action.payload.apiaries || [];
        state.groups = action.payload.groups || [];

        // One hive object per id. Owned apiary rows win; group shares of the
        // same hive must not create a second card or inspection selection.
        const byId = new Map<number, Hive>();
        const apiaryHives = (action.payload.apiaries || []).flatMap(
          (apiary: Location) =>
            (apiary.hives || []).map((hive: Hive) => ({
              ...hive,
              label: hive.name,
              location_name: apiary.name,
            })),
        );
        apiaryHives.forEach((hive: Hive) => {
          byId.set(hive.id, hive);
        });
        (action.payload.groups || []).forEach((group: Group) => {
          (group.hives || []).forEach((hive: Hive) => {
            if (!byId.has(hive.id)) {
              byId.set(hive.id, {
                ...hive,
                label: hive.name,
                group_name: group.name,
              });
            }
          });
        });
        state.hives = Array.from(byId.values());
      })
      .addCase(fetchApiariesAndGroups.rejected, (state, action) => {
        state.loadingState = "failed";
        state.error = action.payload as string;
      })

      // Create Apiary
      .addCase(createApiary.pending, (state) => {
        state.loadingState = "pending";
        state.error = null;
      })
      .addCase(createApiary.fulfilled, (state, action) => {
        state.loadingState = "succeeded";
        state.apiaries.push(action.payload);

        // Add new hives to state.hives
        if (action.payload.hives && action.payload.hives.length > 0) {
          const newHives = action.payload.hives.map((hive: Hive) => ({
            ...hive,
            label: hive.name,
          }));
          state.hives.push(...newHives);
        }

        state.apiaryEdited = false;
      })
      .addCase(createApiary.rejected, (state, action) => {
        state.loadingState = "failed";
        state.error = action.payload as string;
      })

      // Update Apiary
      .addCase(updateApiary.fulfilled, (state, action) => {
        const index = state.apiaries.findIndex(
          (apiary) => apiary.id === action.payload.data.id,
        );
        if (index !== -1) {
          state.apiaries[index] = action.payload.data;
        }
        state.apiaryEdited = false;
      })

      // Delete Apiary
      .addCase(deleteApiary.fulfilled, (state, action) => {
        state.apiaries = state.apiaries.filter(
          (apiary) => apiary.id !== action.payload,
        );
      })

      // Create Hive
      .addCase(createHive.fulfilled, (state, action) => {
        const apiary = state.apiaries.find(
          (a) => a.id === action.payload.data.location_id,
        );
        if (apiary) {
          apiary.hives.push(action.payload.data);
        }
        state.hives.push({
          ...action.payload.data,
          label: action.payload.data.name,
        });
      })

      // Update Hive
      .addCase(updateHive.fulfilled, (state, action) => {
        // Update in apiaries
        const apiary = state.apiaries.find(
          (a) => a.id === action.payload.data.location_id,
        );
        if (apiary) {
          const hiveIndex = apiary.hives.findIndex(
            (h) => h.id === action.payload.data.id,
          );
          if (hiveIndex !== -1) {
            apiary.hives[hiveIndex] = action.payload.data;
          }
        }

        // Update in hives array
        const hiveIndex = state.hives.findIndex(
          (h) => h.id === action.payload.data.id,
        );
        if (hiveIndex !== -1) {
          state.hives[hiveIndex] = {
            ...action.payload.data,
            label: action.payload.data.name,
          };
        }
      })

      // Delete Hive
      .addCase(deleteHive.fulfilled, (state, action) => {
        // Remove from apiaries
        state.apiaries.forEach((apiary) => {
          apiary.hives = apiary.hives.filter(
            (hive) => hive.id !== action.payload,
          );
        });

        // Remove from hives array
        state.hives = state.hives.filter((hive) => hive.id !== action.payload);
      });
  },
});

export const {
  setFilters,
  clearFilters,
  setView,
  setSelectedHive,
  setSelectedApiary,
  setApiaryEdited,
  clearError,
  resetState,
} = locationsSlice.actions;

export default locationsSlice.reducer;
